import { VoyageRoutePointFeature, VoyageRouteLineFeature } from '../models/voyage-routing-model';
import { VoyagePlan } from '../models/vessel-planning-model';
import { knotsToMps, mpsToKnots } from '../utils/helpers';
import { ImportResult } from './types/converters.types';
import { isPilotPointName } from './helpers';
import { DateTime } from 'luxon';
import { DOMParser as XMLDOMParser } from '@xmldom/xmldom';

export enum RTZVersion {
    RTZ_1_0 = "1.0",
    RTZ_1_1 = "1.1",
    RTZ_1_2 = "1.2",
    RTZ_2_0 = "2.0"
};

type RTZCheckResult = { doc: Document; version: RTZVersion };
type RTZValidationOptions = { filename?: string };
type RTZValidationResult = { valid: boolean; errors: string[]; version?: RTZVersion };

const namespaceToVersion: Record<string, RTZVersion> = {
    'http://www.cirm.org/RTZ/1/0': RTZVersion.RTZ_1_0,
    'http://www.cirm.org/RTZ/1/1': RTZVersion.RTZ_1_1,
    'http://www.cirm.org/RTZ/1/2': RTZVersion.RTZ_1_2,
    'http://www.cirm.org/RTZ/2/0': RTZVersion.RTZ_2_0,
    // Legacy/test namespace
    'http://www.acme.com/RTZ/1/0': RTZVersion.RTZ_1_0
};

const versionToEnum: Record<string, RTZVersion> = {
    "1.0": RTZVersion.RTZ_1_0,
    "1.1": RTZVersion.RTZ_1_1,
    "1.2": RTZVersion.RTZ_1_2,
    "2.0": RTZVersion.RTZ_2_0
};

export class RTZConverter {
    static _hasDangerousTags(str: string): boolean {
        // Basic check for dangerous tags or suspicious markup
        const forbiddenTags = /<(script|iframe|object|embed|form|img|svg|input|textarea|audio|video|link|meta|base|frame|frameset|applet|style)(\s|>)/i;
        return forbiddenTags.test(str);
    }

    static check(str: string, options: { throwOnError?: boolean } = {}): RTZCheckResult | undefined {
        try {
            // Reject any input containing probable dangerous HTML/SVG/script tags
            if (RTZConverter._hasDangerousTags(str)) {
                throw new Error("Input file contains forbidden tags.");
            }
            const doc = RTZConverter._parseXmlDocument(str);
            const root = doc.documentElement;

            if (!root || root.nodeName.toLowerCase() !== 'route') {
                return;
            }

            const xmlns = root.getAttribute('xmlns')?.trim();
            if (!xmlns) {
                throw new Error("Invalid RTZ format: route xmlns is missing.");
            }

            const versionAttr = root.getAttribute('version')?.trim();
            if (!versionAttr) {
                throw new Error("Invalid RTZ format: route version is missing.");
            }

            const nsVersion = namespaceToVersion[xmlns];
            if (!nsVersion) {
                throw new Error(`Unsupported RTZ namespace: ${xmlns}`);
            }

            const attrVersion = versionToEnum[versionAttr];
            if (!attrVersion) {
                throw new Error(`Unsupported RTZ version: ${versionAttr}`);
            }

            if (nsVersion !== attrVersion) {
                throw new Error(`RTZ namespace/version mismatch: xmlns=${xmlns} version=${versionAttr}`);
            }

            return { doc, version: attrVersion };
        } catch (e) {
            // don't care
            if (options.throwOnError) {
                throw e;
            }
            console.warn(e);
        }
    }

    static validate(str: string, options: RTZValidationOptions = {}): RTZValidationResult {
        try {
            const parsed = RTZConverter.check(str, { throwOnError: true });
            if (!parsed) {
                return { valid: false, errors: ["Invalid RTZ document."] };
            }
            const errors = RTZConverter._validateDoc(parsed.doc, parsed.version, options);
            return { valid: errors.length === 0, errors, version: parsed.version };
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            return { valid: false, errors: [message] };
        }
    }

    static import(str: string, options: RTZValidationOptions = {}): ImportResult | undefined {
        try {
            const route_geometry: Array<VoyageRoutePointFeature | VoyageRouteLineFeature> = [];

            const parsed = RTZConverter.check(str);
            if (!parsed) return;
            const { doc, version } = parsed;

            const validationErrors = RTZConverter._validateDoc(doc, version, options);
            if (validationErrors.length > 0) {
                throw new Error(`RTZ validation failed: ${validationErrors.join("; ")}`);
            }

            // Version-specific parsing rules can be added here as needed.
            switch (version) {
                case RTZVersion.RTZ_1_0:
                case RTZVersion.RTZ_1_1:
                case RTZVersion.RTZ_1_2:
                case RTZVersion.RTZ_2_0:
                    break;
                default:
                    throw new Error(`Unsupported RTZ version: ${version}`);
            }

            // Get defaultWaypoint for default values
            const dwp = RTZConverter._extractWP(RTZConverter._querySelector(doc, 'defaultWaypoint'));

            const wps = RTZConverter._querySelectorAll(doc, 'waypoint');

            // Loop through and extract waypoints and legs
            // Note: RTZ has leg information on the *end* waypoint (ie. the opposite of expected for our format)
            for (let i = 0; i < wps.length; i++) {
                const waypointEl = wps[i];
                const nextWPEl = i < wps.length - 1 ? wps[i + 1] : null;

                const coords: [number, number] = this._getPosition(waypointEl);

                let nextWP: { [index: string]: string } | null = null;
                if (nextWPEl) {
                    nextWP = {
                        leg_geometryType: 'Loxodrome',  // Default to RL, per RTZ spec.
                        ...dwp,
                        ...RTZConverter._extractWP(nextWPEl)
                    };
                }

                const name = waypointEl.getAttribute('name') || '';
                const wpAttrs = RTZConverter._extractWP(waypointEl);
                const isPilotPoint = isPilotPointName(name);

                const properties: VoyageRoutePointFeature["properties"] = {
                    type: 'Hard',
                    name
                };

                if (isPilotPoint) {
                    properties.pilotPoint = true;
                }

                const wPoint: VoyageRoutePointFeature = {
                    type: 'Feature',
                    geometry: {
                        coordinates: coords,
                        type: 'Point'
                    },
                    properties
                };


                if (nextWP) {
                    if (nextWP["leg_speedMin"] && !isNaN(Number(nextWP["leg_speedMin"]))) {
                        wPoint.properties.speed_minimum = knotsToMps(Number(nextWP["leg_speedMin"]));
                    }

                    if (nextWP["leg_speedMax"] && !isNaN(Number(nextWP["leg_speedMax"]))) {
                        const speedMax = knotsToMps(Number(nextWP["leg_speedMax"]));
                        wPoint.properties.speed_maximum = speedMax;
                        wPoint.properties.userInput = {
                            ...(wPoint.properties.userInput || {}),
                            speed_maximum: speedMax
                        };
                    }

                    // cross-track distance — use smallest of port/starboard, mark as userInput
                    let xtd: number | undefined;
                    if (nextWP["leg_starboardXTD"] && nextWP["leg_portsideXTD"]) {
                        const stbd = Number(nextWP["leg_starboardXTD"]);
                        const port = Number(nextWP["leg_portsideXTD"]);
                        if (!isNaN(stbd) && !isNaN(port)) {
                            xtd = Math.min(stbd, port);
                        }
                    } else if (nextWP["leg_starboardXTD"] || nextWP["leg_portsideXTD"]) {
                        const value = nextWP["leg_starboardXTD"] ?? nextWP["leg_portsideXTD"];
                        const parsed = Number(value);
                        if (!isNaN(parsed)) {
                            xtd = parsed;
                        }
                    }
                    if (xtd !== undefined) {
                        RTZConverter._setImportedUserValue(wPoint, "max_x_track", xtd);
                    }

                    // rate of turn — mark as userInput so optimiser cannot overwrite
                    const rateOfTurn = RTZConverter._toFiniteNumber(nextWP["leg_rateOfTurn"]);
                    if (rateOfTurn !== undefined) {
                        RTZConverter._setImportedUserValue(wPoint, "rate_of_turn", rateOfTurn);
                    }
                }

                // turn radius — mark as userInput so optimiser cannot overwrite
                const radiusNm = RTZConverter._toFiniteNumber(wpAttrs.radius ?? dwp.radius);
                if (radiusNm !== undefined) {
                    RTZConverter._setImportedUserValue(wPoint, "turn_radius", radiusNm);
                }

                route_geometry.push(wPoint);

                if (nextWPEl && nextWP) {
                    route_geometry.push({
                        type: 'Feature',
                        geometry: {
                            coordinates: [coords, this._getPosition(nextWPEl)],
                            type: 'LineString'
                        },
                        properties: {
                            leg_type: nextWP['leg_geometryType'].toLowerCase() === 'orthodrome' ? 'GreatCircle' : 'Rhumbline'
                        }
                    });
                }
            };

            return {
                route: {
                    routeId: '',
                    route_geometry
                },
                extras: {
                    rtz_version: version,
                    import_source: "RTZ"
                    // Plan related data.
                }
            };
        } catch (error) {
            console.warn(error);
        }
    }

    static export(plan: VoyagePlan, filename: string, version: RTZVersion): string {
        // Bail out if no valid route is selected.
        if (!plan.currentSelectedRouteId) {
            throw new Error("No valid route selected in plan!");
        }

        // Get the selected route
        const routeIndex = plan.routes.findIndex(route => route.routeId === plan.currentSelectedRouteId) ?? plan.selectedRouteIndex
        const route = plan.routes[routeIndex];
        let xmlString;
        if (version == RTZVersion.RTZ_1_0) {
            xmlString = '<route xmlns="http://www.cirm.org/RTZ/1/0" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" version="1.0"/>';
        }
        else if (version == RTZVersion.RTZ_1_2) {
            xmlString = '<route xmlns="http://www.cirm.org/RTZ/1/2" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" version="1.2"/>';
        }
        else {
            throw new Error(`Unsupported RTZ export version (supported: 1.0, 1.2): ${version}`);
        }

        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlString, "text/xml"); //important to use "text/xml"
        const xmlRoute = xmlDoc.documentElement;

        const xmlRouteInfo = this._createXMLElement("routeInfo", xmlRoute);

        // Set routeName to the filename for compatibility with legacy/test expectations
        xmlRouteInfo.setAttribute("routeName", filename);
        const imo = (<any>window)["appenv"].vesselIMONumber;
        if (imo) {
            xmlRouteInfo.setAttribute("vesselIMO", imo);
        }

        const xmlWaypoints = this._createXMLElement("waypoints", xmlRoute);
        // Loop through and add waypoints
        let wpIdx = 1;
        for (let i = 0; i < route.route_geometry.length; i++) {
            if (route.route_geometry[i].geometry.type === "Point") {
                const point = <VoyageRoutePointFeature>route.route_geometry[i];
                const xmlWaypoint = this._createXMLElement("waypoint", xmlWaypoints);
                xmlWaypoint.setAttribute("id", "" + wpIdx++);
                xmlWaypoint.setAttribute("revision", "0");
                if (point.properties.name) {
                    xmlWaypoint.setAttribute("name", point.properties.name);
                }

                const xmlPosition = this._createXMLElement("position", xmlWaypoint);
                xmlPosition.setAttribute("lat", "" + point.geometry.coordinates[1]);
                xmlPosition.setAttribute("lon", "" + point.geometry.coordinates[0]);

                const pointUI = point.properties.userInput as any;
                const turnRadius = pointUI?.turn_radius ?? point.properties.turn_radius;
                if (RTZConverter._isFiniteNumber(turnRadius)) {
                    xmlWaypoint.setAttribute("radius", "" + turnRadius);
                }

                if (i > 0) {
                    const xmlLeg = this._createXMLElement("leg", xmlWaypoint);
                    xmlLeg.setAttribute("geometryType", route.route_geometry[i - 1].properties.leg_type === "GreatCircle" ? "Orthodrome" : "Loxodrome");

                    const prevPoint = <VoyageRoutePointFeature>route.route_geometry[i - 2];
                    const prevUI = prevPoint.properties.userInput as any;

                    const speedMin = prevUI?.speed_minimum ?? prevPoint.properties.speed_minimum;
                    if (speedMin) {
                        xmlLeg.setAttribute("speedMin", "" + mpsToKnots(speedMin));
                    }
                    const speedMax = prevUI?.speed_maximum ?? prevPoint.properties.speed_maximum;
                    if (speedMax) {
                        xmlLeg.setAttribute("speedMax", "" + mpsToKnots(speedMax));
                    }
                    const xtd = prevUI?.max_x_track ?? prevPoint.properties.max_x_track;
                    if (RTZConverter._isFiniteNumber(xtd)) {
                        xmlLeg.setAttribute("starboardXTD", "" + xtd);
                        xmlLeg.setAttribute("portsideXTD", "" + xtd);
                    }
                    const rateOfTurn = prevUI?.rate_of_turn ?? prevPoint.properties.rate_of_turn;
                    if (RTZConverter._isFiniteNumber(rateOfTurn)) {
                        xmlLeg.setAttribute("rateOfTurn", "" + rateOfTurn);
                    }
                    const minDepth = prevUI?.min_depth ?? prevPoint.properties.min_depth;
                    if (RTZConverter._isFiniteNumber(minDepth)) {
                        xmlLeg.setAttribute("safetyDepth", "" + minDepth);
                    }
                }
            }
        }

        const xmlSer = new XMLSerializer();
        const result = '<?xml version="1.0" encoding="UTF-8"?>\n' + xmlSer.serializeToString(xmlDoc);

        return result;
    }

    private static _extractWP(wpEl: Element | null): { [index: string]: string } {
        const res: { [index: string]: any } = {};

        if (!wpEl) {
            return {};
        }

        for (const [name, value] of RTZConverter._getAttributeEntries(wpEl)) {
            res[name] = value;
        }

        // Leg
        const legEl = RTZConverter._querySelector(wpEl, 'leg');
        if (legEl) {
            for (const [name, value] of RTZConverter._getAttributeEntries(legEl)) {
                res[`leg_${name}`] = value;
            }
        }

        return res;
    }

    private static _getPosition(wpElement: Element): [number, number] {
        const posEl = RTZConverter._querySelector(wpElement, 'position');

        if (!posEl) {
            throw new Error("Invalid RTZ format; position element is missing from waypoint");
        }

        const lat = posEl.getAttribute("lat");
        if (lat === null) {
            throw new Error("Invalid RTZ format; position element is missing lat attribute");
        }
        if (isNaN(Number(lat))) {
            throw new Error("Invalid RTZ format; lat attribute is not a number");
        }

        const lon = posEl.getAttribute("lon");
        if (lon === null) {
            throw new Error("Invalid RTZ format; position element is missing lon attribute");
        }
        if (isNaN(Number(lon))) {
            throw new Error("Invalid RTZ format; lon attribute is not a number");
        }

        return [Number(lon), Number(lat)];
    }

    private static _createXMLElement(name: string, parent: Element): Element {
        const xmlElement = document.createElementNS(parent.namespaceURI, name);
        parent.appendChild(xmlElement);
        return xmlElement;
    }

    private static _validateDoc(doc: Document, version: RTZVersion, options: RTZValidationOptions = {}): string[] {
        const errors: string[] = [];

        const routeInfo = RTZConverter._querySelector(doc, 'routeInfo');
        if (!routeInfo) {
            errors.push("Missing routeInfo element.");
        }

        const waypointsEl = RTZConverter._querySelector(doc, 'waypoints');
        if (!waypointsEl) {
            errors.push("Missing waypoints element.");
        }

        // No longer require routeName to match filename (import/validation)

        if (routeInfo) {
            const start = routeInfo.getAttribute('validityPeriodStart');
            const stop = routeInfo.getAttribute('validityPeriodStop');
            if (start && stop) {
                const startDt = DateTime.fromISO(start, { setZone: true });
                const stopDt = DateTime.fromISO(stop, { setZone: true });
                if (!startDt.isValid || !stopDt.isValid) {
                    errors.push("Invalid validityPeriodStart/validityPeriodStop date format.");
                } else if (startDt > stopDt) {
                    errors.push("validityPeriodStart is after validityPeriodStop.");
                }
            }

            for (const child of RTZConverter._getChildElements(routeInfo)) {
                if (child.tagName.toLowerCase() === 'extension') {
                    errors.push("routeInfo extension must be wrapped in an extensions element.");
                    break;
                }
            }
        }

        const waypointEls = RTZConverter._querySelectorAll(doc, 'waypoint');
        const waypointIds = new Set<string>();
        for (const wp of waypointEls) {
            const id = wp.getAttribute('id');
            if (!id) {
                errors.push("Waypoint id is missing.");
            } else {
                if (waypointIds.has(id)) {
                    errors.push(`Duplicate waypoint id: ${id}`);
                }
                waypointIds.add(id);
                if (!RTZConverter._isNonNegativeInteger(id)) {
                    errors.push(`Waypoint id must be a non-negative integer: ${id}`);
                }
            }

            const revision = wp.getAttribute('revision');
            if (revision !== null && revision !== '' && !RTZConverter._isNonNegativeInteger(revision)) {
                errors.push(`Waypoint revision must be a non-negative integer: ${revision}`);
            }
        }

        const defaultLeg = RTZConverter._querySelector(doc, 'defaultWaypoint leg');
        if (defaultLeg) {
            const safetyContour = defaultLeg.getAttribute('safetyContour');
            if (safetyContour !== null) {
                const value = Number(safetyContour);
                if (!isFinite(value)) {
                    errors.push("defaultWaypoint leg safetyContour must be a number.");
                } else if (value < 0) {
                    errors.push("defaultWaypoint leg safetyContour must be greater than 0.");
                }
            }
        }

        const legEls = RTZConverter._querySelectorAll(doc, 'leg');
        for (const leg of legEls) {
            const geometryType = leg.getAttribute('geometryType');
            if (geometryType && !['loxodrome', 'orthodrome'].includes(geometryType.toLowerCase())) {
                errors.push(`Invalid leg geometryType: ${geometryType}`);
            }
        }

        const scheduleContainers = RTZConverter._querySelectorAll(doc, 'schedule');
        for (const schedule of scheduleContainers) {
            for (const blockName of ['manual', 'calculated']) {
                const block = RTZConverter._querySelector(schedule, blockName);
                if (!block) continue;
                let lastTime: DateTime | null = null;
                const scheduleElements = RTZConverter._querySelectorAll(block, 'scheduleElement');
                for (const se of scheduleElements) {
                    const waypointId = se.getAttribute('waypointId');
                    if (!waypointId) {
                        errors.push("scheduleElement waypointId is missing.");
                    } else if (waypointIds.size > 0 && !waypointIds.has(waypointId)) {
                        errors.push(`scheduleElement waypointId does not exist in waypoints: ${waypointId}`);
                    }

                    const eta = se.getAttribute('eta');
                    const etd = se.getAttribute('etd');
                    const times: DateTime[] = [];
                    if (eta) {
                        const etaDt = DateTime.fromISO(eta, { setZone: true });
                        if (!etaDt.isValid) {
                            errors.push(`Invalid eta datetime: ${eta}`);
                        } else {
                            times.push(etaDt);
                        }
                    }
                    if (etd) {
                        const etdDt = DateTime.fromISO(etd, { setZone: true });
                        if (!etdDt.isValid) {
                            errors.push(`Invalid etd datetime: ${etd}`);
                        } else {
                            times.push(etdDt);
                        }
                    }

                    if (times.length > 0) {
                        const current = times.reduce((min, t) => t < min ? t : min, times[0]);
                        if (lastTime && current < lastTime) {
                            errors.push("Schedule times are not in non-decreasing order.");
                        }
                        lastTime = current;
                    }

                    const windDirection = se.getAttribute('windDirection');
                    if (windDirection !== null) {
                        const value = Number(windDirection);
                        if (!isFinite(value) || value < 0 || value > 360) {
                            errors.push(`Invalid windDirection: ${windDirection}`);
                        }
                    }
                    const currentDirection = se.getAttribute('currentDirection');
                    if (currentDirection !== null) {
                        const value = Number(currentDirection);
                        if (!isFinite(value) || value < 0 || value > 360) {
                            errors.push(`Invalid currentDirection: ${currentDirection}`);
                        }
                    }
                    const windSpeed = se.getAttribute('windSpeed');
                    if (windSpeed !== null) {
                        const value = Number(windSpeed);
                        if (!isFinite(value) || value < 0) {
                            errors.push(`Invalid windSpeed: ${windSpeed}`);
                        }
                    }
                    const currentSpeed = se.getAttribute('currentSpeed');
                    if (currentSpeed !== null) {
                        const value = Number(currentSpeed);
                        if (!isFinite(value) || value < 0) {
                            errors.push(`Invalid currentSpeed: ${currentSpeed}`);
                        }
                    }
                }
            }
        }

        return errors;
    }

    private static _parseXmlDocument(str: string): Document {
        const parser = new XMLDOMParser();
        const doc = parser.parseFromString(str, 'text/xml');
        const parseError = RTZConverter._querySelector(doc, 'parsererror');
        if (parseError) {
            throw new Error(parseError.textContent?.trim() || 'Invalid XML document.');
        }
        return doc;
    }

    private static _querySelector(node: Document | Element, path: string): Element | null {
        return RTZConverter._querySelectorAll(node, path)[0] ?? null;
    }

    private static _querySelectorAll(node: Document | Element, path: string): Element[] {
        const selectors = path
            .trim()
            .split(/\s+/)
            .map(part => part.trim());

        let currentNodes: Array<Document | Element> = [node];
        for (const selector of selectors) {
            const nextNodes: Element[] = [];
            for (const currentNode of currentNodes) {
                nextNodes.push(...RTZConverter._getDescendantElementsByTagName(currentNode, selector));
            }
            currentNodes = nextNodes;
        }
        return currentNodes.filter((currentNode): currentNode is Element => currentNode.nodeType === 1);
    }

    private static _getDescendantElementsByTagName(node: Document | Element, tagName: string): Element[] {
        const elements = node.getElementsByTagName('*');
        const results: Element[] = [];
        for (let index = 0; index < elements.length; index++) {
            const element = elements.item(index);
            if (element && element.tagName.toLowerCase() === tagName.toLowerCase()) {
                results.push(element);
            }
        }
        return results;
    }

    private static _getChildElements(element: Element): Element[] {
        const children: Element[] = [];
        for (let child = element.firstChild; child; child = child.nextSibling) {
            if (child.nodeType === 1) {
                children.push(child as Element);
            }
        }
        return children;
    }

    private static _getAttributeEntries(element: Element): Array<[string, string | undefined]> {
        const entries: Array<[string, string | undefined]> = [];
        for (let index = 0; index < element.attributes.length; index++) {
            const attribute = element.attributes.item(index);
            if (attribute) {
                entries.push([attribute.name, attribute.value || undefined]);
            }
        }
        return entries;
    }

    private static _isNonNegativeInteger(value: string): boolean {
        if (!/^\d+$/.test(value)) return false;
        const num = Number(value);
        return Number.isInteger(num) && num >= 0;
    }

    /**
     * Safely parse a string to a finite number.
     * Returns undefined if the value is null, undefined, empty, or not finite.
     */
    private static _toFiniteNumber(value: string | null | undefined): number | undefined {
        if (value === null || value === undefined || value === '') return undefined;
        const num = Number(value);
        if (!isFinite(num)) return undefined;
        return num;
    }

    /**
     * True only for finite numbers. Used on export so null/NaN values are
     * omitted instead of being serialised as "null"/"NaN", which ECDIS reject.
     */
    private static _isFiniteNumber(value: unknown): value is number {
        return typeof value === 'number' && isFinite(value);
    }

    /**
     * Assign an RTZ-imported value to a waypoint property AND record it in
     * userInput so the optimisation API treats it as a user-entered value
     * and will not overwrite it in the response merge.
     */
    private static _setImportedUserValue(
        point: VoyageRoutePointFeature,
        key: string,
        value: number
    ): void {
        const props = point.properties as any;
        props[key] = value;
        props.userInput = {
            ...(props.userInput || {}),
            [key]: value
        };
    }
}
